import { Fragment, useEffect } from 'react';
import { ModalHeader } from '@components/modals/modal-header';
import { Transition } from '@headlessui/react';
import classNames from 'classnames';

import { DrawerPosition, DrawerProps } from './drawer.type';

const TRANSLATE_HIDDEN: Record<DrawerPosition, string> = {
  left: '-translate-x-full',
  right: 'translate-x-full',
  top: '-translate-y-full',
  bottom: 'translate-y-full',
};

const SIZE_CLASSES: Record<DrawerPosition, string> = {
  left: 'inset-y-0 left-0 right-auto h-full w-90',
  right: 'inset-y-0 right-0 left-auto h-full w-90',
  top: 'inset-x-0 top-0 bottom-auto w-full h-auto',
  bottom: 'inset-x-0 bottom-0 top-auto w-full h-auto',
};

export const Drawer = ({
  isOpen,
  onClose,
  position = 'right',
  header,
  children,
  className,
  bodyScrollable = false,
  closeOnBackdropClick = true,
  animate = true,
  onAfterClose,
}: DrawerProps) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // When `animate` is false we drop the transition classes (instant show/hide) and keep the node
  // mounted (`unmount={false}` → toggles `hidden`) — the exact legacy behavior, for surfaces where the
  // slide would clash. When true, Headless UI keeps the node mounted through the leave transform, so the
  // 300ms slide plays before unmount (mirrors Modal).
  const backdropTransition = animate
    ? {
        enter: 'transition-opacity duration-300 ease-in-out',
        enterFrom: 'opacity-0',
        enterTo: 'opacity-100',
        leave: 'transition-opacity duration-300 ease-in-out',
        leaveFrom: 'opacity-100',
        leaveTo: 'opacity-0',
      }
    : {};
  const panelTransition = animate
    ? {
        enter: 'transition-transform duration-300 ease-in-out',
        enterFrom: TRANSLATE_HIDDEN[position],
        enterTo: 'translate-x-0 translate-y-0',
        leave: 'transition-transform duration-300 ease-in-out',
        leaveFrom: 'translate-x-0 translate-y-0',
        leaveTo: TRANSLATE_HIDDEN[position],
      }
    : {};

  return (
    <>
      {/* Backdrop */}
      <Transition as={Fragment} show={isOpen} appear unmount={animate} {...backdropTransition}>
        <div
          aria-hidden='true'
          className='fixed inset-0 z-40 bg-black/40'
          onClick={closeOnBackdropClick ? onClose : undefined}
        />
      </Transition>

      {/* `open` is static: the element is only visible while open (or animating out), so the UA
          `dialog:not([open])` display:none rule never fights us — Headless UI owns visibility.
          `afterLeave` lets a parent defer the state teardown that would unmount us until the slide-out
          has finished (mirrors Modal). */}
      <Transition as={Fragment} show={isOpen} appear unmount={animate} afterLeave={onAfterClose} {...panelTransition}>
        <dialog
          open
          aria-modal='true'
          className={classNames(
            'fixed z-50 m-0 p-0 max-w-none max-h-none flex flex-col',
            'bg-surface-primary shadow-xl',
            SIZE_CLASSES[position],
            className
          )}>
          {header && <ModalHeader {...header} handleClose={onClose} />}
          <div
            className={classNames(
              'flex min-h-0 flex-1 flex-col',
              bodyScrollable ? 'overflow-y-auto' : 'overflow-hidden'
            )}>
            {children}
          </div>
        </dialog>
      </Transition>
    </>
  );
};
